import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { 
  Sparkles, Heart, Users, Leaf, Link, Mountain, 
  Home, Zap, RefreshCw, Sprout, Scale, Droplet, 
  Trash2, Bug, Carrot, Bike, PiggyBank, BookOpen, 
  Smile, Compass
} from "lucide-react";
import { IconChartBubble } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { useMurmuration, InitialPosition } from "@/hooks/useMurmuration";

const topics = [
  // Large Pills (4)
  { icon: Sparkles, key: "climate", gradient: "from-brand-teal to-cyan-400", size: "large", delay: "0s" },
  { icon: Leaf, key: "ecodesign", gradient: "from-green-600 to-green-400", size: "large", delay: "0.6s" },
  { icon: Zap, key: "energy", gradient: "from-pink-500 to-purple-500", size: "large", delay: "1.4s" },
  { icon: Bug, key: "biodiversity", gradient: "from-green-700 to-green-500", size: "large", delay: "0.9s" },
  
  // Medium Pills (6)
  { icon: Heart, key: "community", gradient: "from-brand-purple to-purple-400", size: "medium", delay: "0.2s" },
  { icon: Users, key: "diversity", gradient: "from-orange-500 to-orange-300", size: "medium", delay: "0.4s" },
  { icon: RefreshCw, key: "circular", gradient: "from-teal-600 to-teal-400", size: "medium", delay: "1.6s" },
  { icon: Mountain, key: "bioregional", gradient: "from-red-600 to-red-400", size: "medium", delay: "1s" },
  { icon: Bike, key: "transport", gradient: "from-yellow-600 to-yellow-400", size: "medium", delay: "1.3s" },
  { icon: Compass, key: "indigenous", gradient: "from-red-500 to-red-300", size: "medium", delay: "0.1s" },
  
  // Small Pills (6)
  { icon: Sprout, key: "regeneration", gradient: "from-green-500 to-green-300", size: "small", delay: "1.8s" },
  { icon: Carrot, key: "food", gradient: "from-orange-500 to-orange-300", size: "small", delay: "1.1s" },
  { icon: BookOpen, key: "policy", gradient: "from-slate-600 to-slate-400", size: "small", delay: "1.7s" },
  { icon: Scale, key: "fairTrade", gradient: "from-purple-600 to-purple-400", size: "small", delay: "0.3s" },
  { icon: Trash2, key: "waste", gradient: "from-orange-600 to-orange-400", size: "small", delay: "0.7s" },
  { icon: Smile, key: "wellbeing", gradient: "from-pink-600 to-purple-400", size: "small", delay: "1.9s" },
  
  // Extra-Small Pills (4)
  { icon: Link, key: "supplyChain", gradient: "from-yellow-500 to-yellow-300", size: "xs", delay: "0.8s" },
  { icon: Home, key: "building", gradient: "from-blue-600 to-blue-400", size: "xs", delay: "1.2s" },
  { icon: Droplet, key: "water", gradient: "from-blue-500 to-blue-300", size: "xs", delay: "0.5s" },
  { icon: PiggyBank, key: "finance", gradient: "from-green-600 to-green-400", size: "xs", delay: "1.5s" },
] as const;

interface CurriculumSectionProps {
  isExpanded: boolean;
}

const getSizeClasses = (size: string) => {
  const sizeMap = {
    xs: "px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm",
    small: "px-2 py-1 text-xs md:px-4 md:py-2 md:text-sm",
    medium: "px-3 py-1.5 text-xs md:px-6 md:py-3 md:text-base",
    large: "px-4 py-2 text-sm md:px-8 md:py-4 md:text-lg",
  };
  return sizeMap[size as keyof typeof sizeMap] || sizeMap.medium;
};

const getIconSizeClasses = (size: string) => {
  const sizeMap = {
    xs: "w-3 h-3 md:w-3 md:h-3",
    small: "w-3 h-3 md:w-4 md:h-4",
    medium: "w-4 h-4 md:w-5 md:h-5",
    large: "w-5 h-5 md:w-6 md:h-6",
  };
  return sizeMap[size as keyof typeof sizeMap] || sizeMap.medium;
};

export const CurriculumSection = ({ isExpanded }: CurriculumSectionProps) => {
  const { t } = useTranslation();
  const [isDispersed, setIsDispersed] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  const [initialPositions, setInitialPositions] = useState<InitialPosition[]>([]);
  const [returnPositions, setReturnPositions] = useState<InitialPosition[]>([]);
  const [announcement, setAnnouncement] = useState("");
  
  // Detect user's motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Calculate container dimensions with debounced resize
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Use actual available space for ellipse calculation
        const viewportHeight = window.innerHeight * 0.6;
        setDimensions({ 
          width: rect.width || 1200, 
          height: Math.max(viewportHeight, window.innerWidth >= 768 ? 700 : 500)
        });
      }
    };

    // Update on expand
    if (isExpanded) {
      updateDimensions();
    }
    
    // Debounce resize events (300ms) for performance
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDimensions, 300);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [isExpanded]);

  // Use murmuration hook for physics-based animation
  const { pillStates, isTransitioning, setIsTransitioning, fps } = useMurmuration({
    pillCount: topics.length,
    isActive: isDispersed,
    containerDimensions: dimensions,
    initialPositions,
    enableReducedMotion: prefersReducedMotion,
  });



  const handleToggle = () => {
    if (!isDispersed && containerRef.current) {
      // GOING TO CONSTELLATION: Capture flexbox positions
      const containerRect = containerRef.current.getBoundingClientRect();
      const positions: InitialPosition[] = pillRefs.current.map((pillEl) => {
        if (!pillEl) return { x: 0, y: 0 };
        
        const rect = pillEl.getBoundingClientRect();
        
        // Calculate position relative to container CENTRE (not top-left)
        const centerX = (rect.left + rect.width / 2) - (containerRect.left + containerRect.width / 2);
        const centerY = (rect.top + rect.height / 2) - (containerRect.top + containerRect.height / 2);
        
        return { x: centerX, y: centerY };
      });
      
      setInitialPositions(positions);
      setReturnPositions(positions); // Store for return journey
      
      // Announce to screen readers
      if (prefersReducedMotion) {
        setAnnouncement(t("curriculum.constellation.reducedMotion"));
      } else {
        setAnnouncement(t("curriculum.constellation.transitioning"));
        setTimeout(() => {
          setAnnouncement(t("curriculum.constellation.activated"));
        }, 1200);
      }
      
      // Use setTimeout to ensure state is updated before isActive becomes true
      setTimeout(() => {
        setIsDispersed(true);
      }, 0);
    } else {
      // RETURNING TO CLOUD: Capture current constellation positions
      setIsReturning(true);
      
      const currentPositions: InitialPosition[] = pillStates.map((state) => ({
        x: state.x,
        y: state.y,
      }));
      setInitialPositions(currentPositions);
      
      // Announce deactivation
      setAnnouncement(t("curriculum.constellation.deactivated"));
      
      setIsDispersed(false);
      
      // End return transition after 800ms, then switch to flexbox
      setTimeout(() => {
        setIsReturning(false);
      }, 800);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <Collapsible open={isExpanded}>
      <CollapsibleContent className="animate-accordion-down data-[state=closed]:animate-accordion-up">
        <div className="pt-24 pb-24 px-6 bg-muted/30 rounded-xl">
          <div className={cn(
            "w-full mx-auto px-4",
            !isDispersed && "max-w-6xl"
          )}>
            <h3 className="font-clash text-3xl md:text-4xl font-bold text-center mb-4 text-foreground tracking-tight">
              {t("curriculum.headline")}
            </h3>
            <p className="text-lg text-muted-foreground text-center mb-8 max-w-3xl mx-auto">
              {t("curriculum.subheadline")}
            </p>
            
            {/* ARIA Live Region for Announcements */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            >
              {announcement}
            </div>
            
            {/* Network Icon Button */}
            <button
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
              disabled={isTransitioning}
              className={cn(
                "mx-auto block text-muted-foreground mb-12",
                "hover:scale-110 hover:text-foreground transition-all duration-300",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isDispersed && "animate-glow text-primary"
              )}
              aria-label={t("curriculum.constellation.toggle")}
              aria-pressed={isDispersed}
              aria-describedby="constellation-description"
              type="button"
            >
              <IconChartBubble className="w-6 h-6" stroke={1.5} />
            </button>
            
            {/* Visually hidden description */}
            <p id="constellation-description" className="sr-only">
              {isDispersed 
                ? t("curriculum.constellation.deactivated")
                : t("curriculum.constellation.label")}
            </p>
            
            {/* Pills Container - Dual Mode Layout */}
            <div 
              ref={containerRef}
              role="list"
              aria-label={t("curriculum.headline")}
              className={isDispersed || isReturning
                ? "relative overflow-hidden" 
                : "flex flex-wrap justify-center items-center gap-2 md:gap-2.5"
              }
              style={isDispersed ? { height: `${dimensions.height}px` } : undefined}
            >
              {topics.map((topic, i) => {
                const Icon = topic.icon;
                const state = pillStates[i] || { x: 0, y: 0, z: 50, blur: 0, opacity: 1 };
                
                // Ensure minimum opacity for readability
                const minOpacity = 0.7;
                const adjustedOpacity = prefersReducedMotion ? 1 : Math.max(minOpacity, state.opacity);
                
                // Calculate z-based scale for depth perception
                const zScale = 0.35 + (state.z / 100) * 0.15; // Range: 0.35 to 0.50 based on z-depth
                const constellationScale = prefersReducedMotion ? 0.55 : zScale;
                
                return (
                  <div
                    key={topic.key}
                    ref={(el) => (pillRefs.current[i] = el)}
                    className={cn(
                      "flex items-center gap-1.5 md:gap-2 rounded-full",
                      "bg-gradient-to-r text-white font-medium cursor-default shadow-lg",
                      topic.gradient,
                      getSizeClasses(topic.size),
                      (isDispersed || isReturning) && "absolute top-1/2 left-1/2",
                      // Subtle hover glow in constellation view
                      isDispersed && !prefersReducedMotion && "hover:shadow-xl hover:brightness-110"
                    )}
                    style={(isDispersed || isReturning) ? {
                      transform: prefersReducedMotion 
                        ? `translate(calc(-50% + ${state.x}px), calc(-50% + ${state.y}px)) scale(${constellationScale})`
                        : `translate3d(calc(-50% + ${state.x}px), calc(-50% + ${state.y}px), 0) scale(${constellationScale})`,
                      filter: prefersReducedMotion ? 'none' : `blur(${state.blur}px)`,
                      opacity: adjustedOpacity,
                      willChange: 'transform, filter, opacity',
                      transition: isTransitioning || isReturning
                        ? 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out'
                        : 'none',
                    } : undefined}
                    role="listitem"
                    aria-label={t(`curriculum.topics.${topic.key}`)}
                  >
                    <Icon className={getIconSizeClasses(topic.size)} aria-hidden="true" />
                    <span>{t(`curriculum.topics.${topic.key}`)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default CurriculumSection;